import { useContext, useEffect, useRef } from "react";
import { ProjectContext, TProjectContext } from "context/ProjectProvider";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import { QUERY_OPC_IMPORT_LOG_STATUS } from "services/graphQL/queries";
import { onSessionTerminating } from "services/session-state";
import {
  clearOpcExport,
  isOpcExportExpired,
  OPC_EXPORT_TIMEOUT_MS,
  TOpcExportHandle
} from "services/opc-export-state";
import { ImportStatuses } from "utils/utils";

// An import runs for tens of seconds to minutes, so a tighter cadence buys
// nothing. This query only exists while a row is importing.
export const OPC_IMPORT_POLL_INTERVAL = 5000;

// useCIQQuery routes every error through ErrorManager on EVERY tick. The toast
// is deduped by message text, but Sentry.captureException is not — an unguarded
// broken poll would file one event per tick until the cap.
const MAX_CONSECUTIVE_POLL_ERRORS = 5;

type TOpcImportLog = {
  id: string;
  // Narrowed on the way in, as useGetACCImportLogData does for the submittal
  // log. The column is a plain string on the wire; anything outside the enum is
  // treated as "not terminal yet" below.
  status: ImportStatuses;
};

export type OpcImportWatcherProps = {
  handle: TOpcExportHandle;
  // The whole handle rather than just the schedule id: the page needs the
  // importLogId to deep-link the finished import into Change Impact, and the
  // handle is already the one place both ids travel together.
  onFinished: (handle: TOpcExportHandle, status: ImportStatuses) => void;
};

/**
 * Polls one row's import to a terminal status. Renders nothing.
 *
 * One instance per tracked import, keyed by schedule — QUERY_OPC_IMPORT_LOG_STATUS
 * takes a single id, and a component per subscription is React's answer to N of
 * them. Nothing polls when no row is importing.
 */
function OpcImportWatcher({
  handle,
  onFinished
}: Readonly<OpcImportWatcherProps>) {
  const { gqlClientForProject }: TProjectContext = useContext(ProjectContext);

  const hasFinishedRef = useRef(false);
  const consecutivePollErrorsRef = useRef(0);

  const { data, error, startPolling, stopPolling } = useCIQQuery<{
    import_log: Array<TOpcImportLog>;
  }>(QUERY_OPC_IMPORT_LOG_STATUS, {
    client: gqlClientForProject,
    skip: !gqlClientForProject,
    pollInterval: OPC_IMPORT_POLL_INTERVAL,
    variables: { importLogId: handle.importLogId }
  });

  // pollInterval in the options AND an imperative startPolling, as the audit
  // poller does — Apollo drops polling when a query is re-observed, which the
  // mount/unmount of these watchers causes routinely.
  useEffect(() => {
    if (!gqlClientForProject) return undefined;

    startPolling(OPC_IMPORT_POLL_INTERVAL);

    // Stop the moment logout begins, so the poll doesn't keep firing (and 401)
    // through the teardown window.
    const unsubscribeTerminating = onSessionTerminating(() => {
      stopPolling();
      clearOpcExport(handle.scheduleId);
    });

    return () => {
      unsubscribeTerminating();
      stopPolling();
    };
  }, [gqlClientForProject, handle.scheduleId, startPolling, stopPolling]);

  // Wall-clock cap from startedAt, so it is correct across a reload. Clears
  // silently — the row falls back to whatever the server says, which is better
  // than a spinner nothing can stop.
  useEffect(() => {
    if (isOpcExportExpired(handle)) {
      clearOpcExport(handle.scheduleId);
      return undefined;
    }
    const timer = setTimeout(
      () => clearOpcExport(handle.scheduleId),
      OPC_EXPORT_TIMEOUT_MS - (Date.now() - handle.startedAt)
    );
    return () => clearTimeout(timer);
  }, [handle]);

  useEffect(() => {
    if (!error) {
      consecutivePollErrorsRef.current = 0;
      return;
    }
    consecutivePollErrorsRef.current += 1;
    if (consecutivePollErrorsRef.current >= MAX_CONSECUTIVE_POLL_ERRORS) {
      clearOpcExport(handle.scheduleId);
    }
  }, [error, handle.scheduleId]);

  useEffect(() => {
    const row = data?.import_log?.[0];
    if (!row) return;
    // PROCESSING, or any status the backend adds later, means keep waiting. An
    // absent row means the same — it is indistinguishable from "not visible
    // yet", so it must never be read as a failure.
    if (
      row.status !== ImportStatuses.PROCESSED &&
      row.status !== ImportStatuses.FAILED
    ) {
      return;
    }
    if (hasFinishedRef.current) return;

    hasFinishedRef.current = true;
    clearOpcExport(handle.scheduleId);
    onFinished(handle, row.status);
  }, [data, handle, onFinished]);

  return null;
}

export default OpcImportWatcher;
